import Vue from 'vue'
import { saveBlob } from '@/utils/excel'

/**
 * Facebook 投放管理接口。
 *
 * GET  /launch/facebook/ads/asset/siteMapping/list
 * GET  /launch/facebook/ads/asset/list
 * POST /launch/file/upload  directory=launch/facebook-ads
 * POST /launch/facebook/ads/asset/submit
 * GET  /launch/facebook/ads/asset/export
 * GET  /launch/facebook/ads/template/download
 * POST /launch/facebook/ads/import
 * GET  /launch/facebook/ads/list
 * GET  /launch/facebook/ads/export
 */
export const ASSET_TYPE = {
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE'
}

export const UPLOAD_STATUS = {
  PENDING: '待上传',
  UPLOADING: '上传中',
  SUCCESS: '上传成功',
  FAILED: '上传失败'
}

export const IMPORT_STATUS = {
  PENDING: '待处理',
  PROCESSING: '处理中',
  SUCCESS: '创建成功',
  FAILED: '创建失败'
}

export const FB_ADS_DIRECTORY = 'launch/facebook-ads'

const OBJECTIVE_LABELS = {
  OUTCOME_SALES: '销量',
  OUTCOME_LEADS: '潜在客户',
  OUTCOME_TRAFFIC: '流量',
  OUTCOME_AWARENESS: '知名度',
  OUTCOME_ENGAGEMENT: '互动',
  OUTCOME_APP_PROMOTION: '应用推广'
}

const OPTIMIZATION_LABELS = {
  OFFSITE_CONVERSIONS: '转化量最大化',
  VALUE: '转化价值最大化',
  LINK_CLICKS: '链接点击量',
  LANDING_PAGE_VIEWS: '落地页浏览量',
  IMPRESSIONS: '展示次数',
  REACH: '覆盖人数'
}

function http() {
  return Vue.prototype.$http
}

function unwrap(response) {
  const data = response && response.data
  if (data && data.header && data.header.code !== 0) {
    throw new Error(data.header.msg || '请求失败')
  }
  return data
}

function toBizError(error) {
  const data = error && error.response && error.response.data
  if (data && data.header && data.header.msg) {
    return new Error(data.header.msg)
  }
  if (data && typeof data === 'object' && typeof data.msg === 'string') {
    return new Error(data.msg)
  }
  return error instanceof Error ? error : new Error((error && error.message) || '请求失败')
}

async function request(fn) {
  try {
    return unwrap(await fn())
  } catch (error) {
    throw toBizError(error)
  }
}

function authConfig(extra = {}) {
  const token = localStorage.getItem('token') || ''
  const headers = extra.headers ? { ...extra.headers } : {}
  if (token) headers.Authorization = `Bearer ${token}`
  return {
    withCredentials: true,
    ...extra,
    headers
  }
}

function pickValue(item, keys, fallback = '') {
  if (!item || typeof item !== 'object') return fallback
  for (let i = 0; i < keys.length; i++) {
    const value = item[keys[i]]
    if (value != null && value !== '') return value
  }
  return fallback
}

export function withActPrefix(id) {
  const raw = String(id || '').trim()
  if (!raw) return ''
  return /^act_/i.test(raw) ? raw : `act_${raw}`
}

export function displayAccountId(id) {
  return String(id || '').replace(/^act_/i, '')
}

export function accountOptionLabel(item) {
  if (!item) return ''
  const id = displayAccountId(item.accountId)
  return item.webSiteUrl ? `${id} · ${item.webSiteUrl}` : id
}

export function mapAssetTypeLabel(type) {
  return String(type).toUpperCase() === ASSET_TYPE.IMAGE ? '广告图片' : '广告视频'
}

export function mapUploadStatus(status) {
  const text = String(status == null ? '' : status).trim()
  if (text === UPLOAD_STATUS.SUCCESS || text === '2' || Number(status) === 2) {
    return { label: '上传成功', type: 'success' }
  }
  if (text === UPLOAD_STATUS.UPLOADING || text === '1' || Number(status) === 1) {
    return { label: '上传中', type: 'warning' }
  }
  if (text === UPLOAD_STATUS.PENDING) {
    return { label: '待上传', type: 'info' }
  }
  if (text === UPLOAD_STATUS.FAILED || text === '3' || Number(status) === 3) {
    return { label: '上传失败', type: 'danger' }
  }
  return { label: text || '未知状态', type: 'info' }
}

export function mapAdStatus(status) {
  const text = String(status == null ? '' : status).trim()
  if (text === IMPORT_STATUS.SUCCESS || text === '已创建') {
    return { label: text === '已创建' ? '已创建' : '创建成功', type: 'success' }
  }
  if (text === IMPORT_STATUS.PROCESSING || text === '创建中') {
    return { label: text === '创建中' ? '创建中' : '处理中', type: 'warning' }
  }
  if (text === IMPORT_STATUS.PENDING) {
    return { label: '待处理', type: 'info' }
  }
  if (text === IMPORT_STATUS.FAILED || text === '失败') {
    return { label: text === '失败' ? '失败' : '创建失败', type: 'danger' }
  }
  return { label: text || '未知状态', type: 'info' }
}

export function isAssetPending(status) {
  const text = String(status == null ? '' : status).trim()
  return text === UPLOAD_STATUS.PENDING || text === UPLOAD_STATUS.UPLOADING || Number(status) === 1
}

export function isAdPending(status) {
  const text = String(status == null ? '' : status).trim()
  return text === IMPORT_STATUS.PENDING || text === IMPORT_STATUS.PROCESSING
}

export function mapObjectiveLabel(value) {
  const raw = String(value || '').trim()
  return OBJECTIVE_LABELS[raw] || raw || '-'
}

export function mapOptimizationLabel(value) {
  const raw = String(value || '').trim()
  return OPTIMIZATION_LABELS[raw] || raw || '-'
}

export function formatUsd(amount) {
  if (amount == null || amount === '') return '-'
  const value = Number(amount)
  if (Number.isNaN(value)) return String(amount)
  const text = Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return `US$${text}`
}

export function centsToUsd(cents) {
  if (cents == null || cents === '') return null
  const value = Number(cents)
  if (Number.isNaN(value)) return null
  return value / 100
}

export function formatCents(cents) {
  const usd = centsToUsd(cents)
  return usd == null ? '-' : formatUsd(usd)
}

function pickList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  const nested = data.list || data.records || data.content || data.rows || data.data
  return Array.isArray(nested) ? nested : []
}

function pickTotal(body, list) {
  if (!body || typeof body !== 'object') return list.length
  if (typeof body.total === 'number') return body.total
  const data = body.data
  if (data && typeof data === 'object') {
    if (typeof data.total === 'number') return data.total
    if (typeof data.totalElements === 'number') return data.totalElements
  }
  return list.length
}

function normalizePagedList(response, mapItem) {
  const body = (response && response.body) || {}
  const list = pickList(body.data).map((item) => (mapItem ? mapItem(item) : item))
  return {
    ...response,
    body: {
      ...body,
      data: list,
      total: pickTotal(body, list)
    }
  }
}

function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utfMatch) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/"/g, ''))
    } catch (error) {
      return utfMatch[1]
    }
  }
  const asciiMatch = disposition.match(/filename="?([^"]+)"?/i)
  return asciiMatch ? asciiMatch[1] : fallback
}

function downloadByUrl(url, filename) {
  const link = document.createElement('a')
  link.href = url
  if (filename) link.download = filename
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

async function parseBlobPayload(blob) {
  const text = await blob.text()
  try {
    return JSON.parse(text)
  } catch (error) {
    return null
  }
}

async function downloadExport(url, params, fallbackName) {
  try {
    const response = await http().get(url, authConfig({
      params,
      responseType: 'blob',
      timeout: 60000
    }))
    const blob = response.data
    const contentType = String(response.headers['content-type'] || '')

    if (contentType.includes('application/json') || blob.type === 'application/json') {
      const json = await parseBlobPayload(blob)
      if (!json) throw new Error('导出失败')
      if (json.header && json.header.code !== 0) {
        throw new Error(json.header.msg || '导出失败')
      }
      const fileUrl = json.body && json.body.data
      if (typeof fileUrl === 'string' && fileUrl) {
        downloadByUrl(fileUrl, fallbackName)
        return { mode: 'url' }
      }
      return { mode: 'empty' }
    }

    const filename = filenameFromDisposition(response.headers['content-disposition'], fallbackName)
    saveBlob(blob, filename)
    return { mode: 'blob' }
  } catch (error) {
    throw toBizError(error)
  }
}

function normalizeAccountId(id) {
  return withActPrefix(id)
}

function normalizeAccountList(data) {
  return (data || [])
    .map((item) => {
      if (item == null) return null
      if (typeof item === 'string' || typeof item === 'number') {
        const accountId = normalizeAccountId(item)
        return accountId ? { accountId, webSiteUrl: '' } : null
      }
      const accountId = normalizeAccountId(pickValue(item, ['accountId', 'adAccountId', 'ad_account_id']))
      if (!accountId) return null
      return {
        id: item.id,
        accountId,
        webSiteUrl: String(pickValue(item, ['webSiteUrl', 'websiteUrl', 'site'])).trim(),
        createTime: pickValue(item, ['createTime', 'create_time'])
      }
    })
    .filter(Boolean)
}

function normalizeAsset(item) {
  const sourceFileName = String(pickValue(item, ['sourceFileName', 'assetName', 'fileName']))
  const createTime = pickValue(item, ['createTime', 'create_time', 'uploadTime'])
  const assetId = String(pickValue(item, ['assetId', 'id']))
  const accountId = normalizeAccountId(pickValue(item, ['accountId', 'adAccountId', 'ad_account_id']))
  return {
    ...item,
    assetId,
    accountId,
    webSiteUrl: String(pickValue(item, ['webSiteUrl', 'websiteUrl'])).trim(),
    assetType: String(pickValue(item, ['assetType'], ASSET_TYPE.VIDEO)).toUpperCase(),
    sourceFileName,
    assetName: sourceFileName,
    campaignName: pickValue(item, ['campaignName', 'campaign_name']),
    uploadStatus: pickValue(item, ['uploadStatus', 'upload_status']),
    uploadTime: createTime,
    createTime
  }
}

function normalizeAd(item) {
  const dailyCents = pickValue(item, ['campaign_daily_budget', 'campaignDailyBudget', 'dailyBudget'], null)
  const bidCents = pickValue(item, ['bid_amount', 'bidAmount'], null)
  return {
    ...item,
    id: pickValue(item, ['id']),
    accountId: normalizeAccountId(pickValue(item, ['ad_account_id', 'adAccountId', 'accountId'])),
    campaignName: pickValue(item, ['campaign_name', 'campaignName']),
    objective: pickValue(item, ['objective']),
    optimizationGoal: pickValue(item, ['optimization_goal', 'optimizationGoal']),
    campaignDailyBudget: dailyCents,
    bidAmountCents: bidCents,
    dailyBudget: centsToUsd(dailyCents),
    bidAmount: centsToUsd(bidCents),
    finalUrl: pickValue(item, ['landing_url', 'landingUrl', 'finalUrl']),
    uploadTime: pickValue(item, ['create_time', 'createTime', 'uploadTime']),
    importStatus: pickValue(item, ['import_status', 'importStatus', 'uploadStatus']),
    statusDesc: pickValue(item, ['error_message', 'errorMessage', 'statusDesc'])
  }
}

export async function listAccounts() {
  const data = await request(() =>
    http().get('/launch/facebook/ads/asset/siteMapping/list', authConfig())
  )
  return {
    ...data,
    body: {
      ...(data.body || {}),
      data: normalizeAccountList(data.body && data.body.data)
    }
  }
}

export async function listAssets(params = {}) {
  const query = {
    pageNum: params.pageNum,
    pageSize: params.pageSize
  }
  if (params.accountId) query.accountId = normalizeAccountId(params.accountId)
  const data = await request(() =>
    http().get('/launch/facebook/ads/asset/list', authConfig({ params: query }))
  )
  return normalizePagedList(data, normalizeAsset)
}

export async function uploadFiles(files, directory = FB_ADS_DIRECTORY) {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  form.append('directory', directory)
  const data = await request(() =>
    http().post('/launch/file/upload', form, authConfig({
      timeout: 10 * 60 * 1000
    }))
  )
  const urls = (data.body && data.body.data) || []
  if (!urls.length) {
    throw new Error('文件上传成功，但未返回素材地址')
  }
  return urls
}

export async function submitAssets({ accountId, assetType, urls, webSiteUrl }) {
  const id = withActPrefix(accountId)
  const payload = {
    accountId: id,
    adAccountId: id,
    assetType,
    urls
  }
  if (webSiteUrl) payload.webSiteUrl = webSiteUrl
  return request(() =>
    http().post('/launch/facebook/ads/asset/submit', payload, authConfig({
      timeout: 60000
    }))
  )
}

export async function uploadAndSubmitAssets({ accountId, assetType, files, webSiteUrl }) {
  const urls = await uploadFiles(files)
  return submitAssets({ accountId, assetType, urls, webSiteUrl })
}

export async function exportAssets(params = {}) {
  const query = {
    pageNum: params.pageNum || 1,
    pageSize: params.pageSize || 15
  }
  if (params.accountId) query.accountId = normalizeAccountId(params.accountId)
  return downloadExport('/launch/facebook/ads/asset/export', query, 'Facebook素材列表.xlsx')
}

export async function downloadAdTemplate() {
  const data = await request(() =>
    http().get('/launch/facebook/ads/template/download', authConfig())
  )
  const url = data.body && data.body.data
  if (!url) throw new Error('未获取到模板地址')
  downloadByUrl(url, 'Facebook广告创建模板.xlsx')
  return url
}

export async function exportAds(params = {}) {
  const query = {}
  if (params.accountId) query.adAccountId = normalizeAccountId(params.accountId)
  return downloadExport('/launch/facebook/ads/export', query, 'Facebook广告列表.xlsx')
}

export async function importAds(file) {
  const form = new FormData()
  form.append('file', file)
  return request(() =>
    http().post('/launch/facebook/ads/import', form, authConfig({
      timeout: 10 * 60 * 1000
    }))
  )
}

export async function listAds(params = {}) {
  const query = {
    pageNum: params.pageNum,
    pageSize: params.pageSize
  }
  if (params.accountId) query.adAccountId = normalizeAccountId(params.accountId)
  const data = await request(() =>
    http().get('/launch/facebook/ads/list', authConfig({ params: query }))
  )
  return normalizePagedList(data, normalizeAd)
}

export function extractImportCampaignNames(rows) {
  return (rows || [])
    .map((row) => String(
      row['系列名称'] ||
      row['广告系列名称'] ||
      row.campaign_name ||
      row.campaignName ||
      ''
    ).trim())
    .filter(Boolean)
}
